import { gql } from '@apollo/client';

export const GET_FORWARD_STOCKING_SKUS = gql`
    query GetForwardStockingSkus($client_id: uuid!) {
        forward_stocking_sku_summary(where: { client_id: { _eq: $client_id } }, order_by: { sku: asc }) {
            sku
            description
            shipper_count
            shipper_name
            inbound_count
            on_hand_count
            outbound_count
            return_count
            total_count
            max_dwell_days
            inventory_min
            inventory_max
        }
    }
`;

export const GET_FORWARD_STOCKING_SKU_ITEMS = gql`
    query GetForwardStockingSkuItems($client_id: uuid!, $sku: String!) {
        forward_stocking_items(
            where: { client_id: { _eq: $client_id }, sku: { _eq: $sku } }
            order_by: { received_into_warehouse_time: desc_nulls_last }
        ) {
            item_id
            status
            return_stage
            latest_event_type
            shipper_id
            carrier_id
            is_return
            has_exception
            received_into_warehouse_time
            dwell_days
            carrier_name
            warehouse_name
            item {
                item_id
                serial_number
                line_id
                pallet {
                    pallet_id
                    pallet_name
                    pallet_number
                    warehouse_location
                }
            }
        }
    }
`;

// Serial numbers and line item ids live on the items behind each SKU, and those
// are fetched lazily per expanded row — so for a SKU the user has not opened,
// they are not in the browser at all and no client-side filter can reach them.
// This asks the server which SKUs (and which items) a term hits.
//
// Both fields hang off the `item` relationship rather than the view itself; the
// view exposes neither. Limited because a short term against line ids — which
// are often two characters — matches broadly.
export const GET_FORWARD_STOCKING_ITEM_MATCHES = gql`
    query GetForwardStockingItemMatches($client_id: uuid!, $term: String!, $limit: Int!) {
        forward_stocking_items(
            where: {
                client_id: { _eq: $client_id }
                _or: [{ item: { serial_number: { _ilike: $term } } }, { item: { line_id: { _ilike: $term } } }]
            }
            limit: $limit
        ) {
            item_id
            sku
        }
    }
`;

export const GET_FORWARD_STOCKING_SUMMARY = gql`
    query GetForwardStockingSummary($client_id: uuid!) {
        client_forward_stocking_summary(where: { client_id: { _eq: $client_id } }) {
            inbound_count
            on_hand_count
            outbound_count
            return_count
            return_created_count
            return_in_transit_count
            return_pending_put_away_count
            return_stored_count
            return_trailer_scheduled_count
            return_picked_count
            return_staged_count
            return_loaded_count
        }
    }
`;

// The options the filter offers: one row per distinct party in the viewer's
// forward stocked inventory. distinct_on has to lead the order_by, so these
// come back id-ordered and are sorted by name client-side.
export const GET_FORWARD_STOCKING_CARRIER_OPTIONS = gql`
    query GetForwardStockingCarrierOptions($client_id: uuid!) {
        forward_stocking_items(
            where: { client_id: { _eq: $client_id } }
            distinct_on: [carrier_id]
            order_by: { carrier_id: asc }
        ) {
            carrier_id
            carrier_name
        }
    }
`;

export const GET_FORWARD_STOCKING_SHIPPER_OPTIONS = gql`
    query GetForwardStockingShipperOptions($client_id: uuid!) {
        forward_stocking_items(
            where: { client_id: { _eq: $client_id } }
            distinct_on: [shipper_id]
            order_by: { shipper_id: asc }
        ) {
            shipper_id
            shipper_name
        }
    }
`;

// The items behind a filtered selection. The roll-up views carry no carrier or
// shipper grain, so the SKU rows and KPI cards are re-counted from these.
//
// Deliberately unlimited, unlike the serial search: a truncated result would
// undercount every SKU and card.
export const GET_FORWARD_STOCKING_FACET_ITEMS = gql`
    query GetForwardStockingFacetItems($where: forward_stocking_items_bool_exp!) {
        forward_stocking_items(where: $where) {
            sku
            status
            return_stage
            dwell_days
            shipper_id
            shipper_name
        }
    }
`;
